<template>
  <v-row
    class="px-6 mt-2 fill-height"
    v-if="
      currentResultsLog &&
      currentResultsLog.results &&
      currentResultsLog.results.threedEphemerides
    "
  >
    <v-col cols="12 px-0 mt-2">
      <v-select
        :items="entitiesList.concat([{ id: 'Earth', name: 'Earth' }])"
        item-value="id"
        item-text="name"
        hide-details
        v-model="cameraView"
        v-on:change="updateCameraView()"
        label="Camera"
      >
      </v-select>
    </v-col>
    <v-col cols="12 px-2 mt-2" style="height: 80vh">
      <div id="FirstViewer" ref="cesiumContainer">
        <cesium-lighting-button ref="cesiumLightingButton" :viewer="viewer" />
        <timeline
          v-if="initialDate && finalDate && timelineInDOM"
          :satEvents="satellitesEvents"
          :initialDate="initialDate"
          :finalDate="finalDate"
          :clock="viewer.clock"
          :updateSatelliteState="updateSatelliteState"
          :toggleTimeline="toggleEventsTimeline"
          :hideTimeline="hideTimeline"
        />
        <spacecraft-panel
          v-if="spacecraftPanelInDOM"
          :updateStyle="updateElementsStyle"
          :toggleSCPanel="toggleSCPanel"
          :hideSCPanel="hideSCPanel"
        >
          <div class="vertical-progress-indicator">
            <v-icon
              name="battery-three-quarters"
              scale="1.5"
              title="battery state"
            ></v-icon>
            <v-progress-linear
              class="vertical-progress-bar"
              :value="satelliteState.battery"
              height="6"
              color="yellow"
            ></v-progress-linear>
          </div>
          <div class="vertical-progress-indicator">
            <v-icon
              name="gas-pump"
              scale="1.5"
              title="remaining propellant"
              class="remaining-propellant-icon"
            ></v-icon>
            <v-progress-linear
              class="vertical-progress-bar"
              :value="satelliteState.remainingPropellantPercentage"
              height="6"
            ></v-progress-linear>
          </div>
        </spacecraft-panel>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import dataTools from '../../tools/data'
import Vue from 'vue'
import * as Cesium from 'cesium'
import 'cesium/Widgets/widgets.css'
import { DataSourceCreation } from './DataSourceCreationFunctions'
import { ThreeDInterface } from './ThreeDInterface'
import { DataPreparation } from './DataPreparation'
import './ThreeD.scss'
import { CesiumInitialization } from './CesiumInitialization'
import timeline from './Timeline.vue'
import SpacecraftPanel from './SpacecraftPanel.vue'
import 'vue-awesome/icons'
import Icon from 'vue-awesome/components/Icon'
import CesiumLightingButton from '@/components/ThreeD/CesiumLightingButton.vue'

export default {
  name: 'three-d',
  mixins: [
    DataSourceCreation,
    ThreeDInterface,
    DataPreparation,
    CesiumInitialization
  ],
  components: {
    timeline,
    SpacecraftPanel,
    'v-icon': Icon,
    'cesium-lighting-button': CesiumLightingButton
  },
  data() {
    return {
      satellitesEvents: [
        {
          satId: '',
          burns: [],
          eclipses: []
        }
      ],
      initialDate: '',
      finalDate: '',
      czml: [],
      entities: [],
      entitiesList: [],
      data: [],
      generalData: [],
      sampledRemainingPropellant: null,
      sampledBatteryState: null,
      satelliteState: {
        remainingPropellant: 0,
        remainingPropellantPercentage: 0,
        battery: 0
      },
      mapboxToken:
        'pk.eyJ1IjoiZXhvdHJhaWwiLCJhIjoiY2szcTNnZmFkMDkzbzNtcDhzMm0ydmJ5YyJ9.5so1MY_ou6yoPmLAzLchUg',
      contextOptions: null,
      cartesianData: [],
      sampledQuaternions: [],
      cameraView: 'Earth',
      viewer: null,
      cesium: null,
      sampledSunDir: [],
      thrusterQuaternions: [],
      sampledTVertexPos: [],
      sampledXAxisVertexPos: [],
      sampledYAxisVertexPos: [],
      sampledZAxisVertexPos: [],
      thrustDates: [],
      eclipseDates: [],
      eclipseIntervals: null,
      thrustManeuvers: null,
      burnProgress: 0,
      lastUpdated: null,
      displayTimeline: false,
      displaySCPanel: false,
      spacecraftPanelInDOM: false,
      timelineInDOM: false,
      threeDModel: null
    }
  },

  watch: {
    currentResultsLog(val) {
      if (val && val.results !== null) {
        Vue.nextTick(() => {
          this.cesiumReady()
        })
      }
    }
  },

  async mounted() {
    this.loadCesium().then(() => {
      this.updateElementsStyle('#FirstViewer')
      window.addEventListener('resize', () => {
        this.updateElementsStyle('#FirstViewer')
      })
    })
  },

  computed: {
    ...mapGetters([
      'userSettings',
      'defaultUserSettings',
      'localDefaultUserSettings',
      'currentMission',
      'currentResultsLog'
    ])
  },

  methods: {
    ...mapActions(['loadResultsLog']),

    injectCSSInInfobox() {
      setTimeout(() => {
        const iframe = this.viewer.infoBox.frame
        iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts')
        var doc = iframe.contentDocument
        var style = doc.createElement('style')
        style.appendChild(
          doc.createTextNode(
            '.cesium-infoBox-description table tbody tr td:last-child {text-align: right;}' +
              '.cesium-infoBox-description table tbody tr td:first-child {font-weight: bold;}'
          )
        )
        doc.head.appendChild(style)
      }, 200)
    },

    updateCameraView() {
      // false if Cesium's clock was paused, true otherwise
      const playAfterCameraMove = this.viewer.clock.shouldAnimate
      if (this.cameraView === 'Earth') {
        // stop tracking other entities
        this.viewer.trackedEntity = null
        // resets camera view and focuses Earth
        this.viewer.camera.flyHome()
        this.viewer.clock.shouldAnimate = playAfterCameraMove
      } else {
        // pause Cesium's clock
        this.viewer.clock.shouldAnimate = false

        // track selected entity
        const entityToTrack = this.entities.getById(this.cameraView)

        // retrieves entity's position as cartographic values (lat, long, alt)
        let satPosInCartographic = new Cesium.Cartographic.fromCartesian(
          entityToTrack.position.getValue(this.viewer.clock.currentTime),
          this.ellipsoid
        )
        // add 10km of altitude
        satPosInCartographic.height += 10e3

        let cameraPositionToGoTo = new Cesium.Cartesian3()
        // convert back to Cartesian
        Cesium.Cartographic.toCartesian(
          satPosInCartographic,
          this.ellipsoid,
          cameraPositionToGoTo
        )

        // fly to 10km above entity's position
        this.viewer.camera.flyTo({
          destination: cameraPositionToGoTo,
          orientation: {
            heading: Cesium.Math.toRadians(15.0),
            pitch: Cesium.Math.toRadians(-60),
            roll: 0.0
          },
          // once flight is complete :
          complete: () => {
            // tracking ground stations is useless
            if (!entityToTrack.id.includes('_GroundStation'))
              this.viewer.trackedEntity = entityToTrack
            // animate again or keep clock paused if it was before calliong this function
            this.viewer.clock.shouldAnimate = playAfterCameraMove
          }
        })
      }
    },

    async cesiumReady() {
      delete this.cartesianData
      this.cartesianData = []
      delete this.data
      // delete CesiumInstance
      if (!this.viewer) {
        await this.loadCesium()
      }
    },

    async loadCesium() {
      if (!Cesium) {
        return
      }
      this.initViewer()
      await this.retrieveData()
      if (!this.currentResultsLog || !this.currentResultsLog.results) {
        return
      }
      this.setInitialDate(this.currentResultsLog.results.missionDateTime)
      // prepares 3D data for visual elements (fills quaternion sampled property)
      this.processPositionAndAttitudeData()
      if (this.generalData.length > 0) {
        this.initSampledProperties()
        // prepares satellite data for satellite state
        this.processGeneralData()
        if (
          !this.sampledRemainingPropellant.isConstant ||
          !this.sampledBatteryState.isConstant
        ) {
          this.spacecraftPanelInDOM = true
        }
        if (this.thrustDates.length > 0 || this.eclipseDates.length > 0) {
          this.timelineInDOM = true
        }
        // fills thrust maneuvers time interval collection
        this.createThrustManeuvers()
        // fills eclipse time interval collection
        this.createEclipseIntervals()
        // attach event listener to viewer's clock to update satellite state
        this.attachSatStateUpdateEventListener()
      }
      // add dataSource to viewer to display entities
      this.addMainDataSource()
      // hides Cesium Ion logo (since we do not use Ion)
      document.querySelector('.cesium-credit-logoContainer').style.display =
        'none'
      this.injectCSSInInfobox()
      this.updateElementsStyle('#FirstViewer')
    },

    initViewer() {
      // Performances improvements :
      this.contextOptions = {
        allowTextureFilterAnisotropic: false,
        webgl: {
          alpha: false,
          depth: true,
          stencil: false,
          antialias: false, // This one is the only I've modified to false from true, the rest are at default values
          premultipliedAlpha: true,
          preserveDrawingBuffer: false,
          failIfMajorPerformanceCaveat: true
        }
      }

      let viewerContainer = this.$refs.cesiumContainer
      // viewerContainer.style.position = 'absolute'
      if (!viewerContainer) {
        return
      }
      let earthRadiusEGM96 = 6378136.3
      this.ellipsoid = new Cesium.Ellipsoid(
        earthRadiusEGM96,
        earthRadiusEGM96,
        earthRadiusEGM96
      )
      let globe = new Cesium.Globe(this.ellipsoid)

      const offlineConfig =
        this.userSettings?.standardConfigurations?.['Cesium offline'] ??
        this.defaultUserSettings?.standardConfigurations?.['Cesium offline'] ??
        this.localDefaultUserSettings?.standardConfigurations?.[
          'Cesium offline'
        ] ??
        false
      const offline = offlineConfig === true

      // initializes main viewer with desired options
      this.viewer = new Cesium.Viewer(viewerContainer, {
        imageryProviderViewModels: offline
          ? this.createCesiumImageLayerOffline()
          : this.createCesiumImageLayerWithoutIon(),
        terrainProviderViewModels: [],
        animation: true,
        baseLayerPicker: true,
        timeline: true,
        geocoder: false,
        homeButton: true,
        navigationHelpButton: false,
        sceneModePicker: true,
        contextOptions: this.contextOptions,
        requestRenderMode: false,
        fullscreenButton: true,
        CreditDisplay: false,
        globe: globe,
        skyBox: false
      })
      this.viewer.scene.fxaa = false
      let creditLogoContainer = viewerContainer.querySelector(
        '.cesium-credit-logoContainer'
      )
      creditLogoContainer.style.display = 'none'
      this.viewer.fullscreenButton.viewModel.command.afterExecute.addEventListener(
        () => this.toggleFullscreen(viewerContainer, undefined)
      )
      document.addEventListener('fullscreenchange', (__) => {
        if (!this.viewer.fullscreenButton.viewModel.isFullscreen) {
          this.disableFullScreen(viewerContainer, undefined)
        }
      })
      this.insertElementInToolbar(this.$refs.cesiumLightingButton.$el)
    },

    enableFullScreen() {
      const cesiumContainer = this.$refs.cesiumContainer
      if (cesiumContainer) {
        CesiumInitialization.methods.enableFullScreen.bind(this)(
          cesiumContainer,
          null
        )
        this.updateElementsStyle('#FirstViewer')
      }
    },

    disableFullScreen() {
      const cesiumContainer = this.$refs.cesiumContainer
      if (cesiumContainer) {
        CesiumInitialization.methods.disableFullScreen.bind(this)(
          cesiumContainer,
          null
        )
        // could not find a better solution to place the currentTimeIndicator correctly than this workaround
        setTimeout(() => {
          this.updateElementsStyle('#FirstViewer')
        }, 1000)
      }
    },

    async retrieveData() {
      await this.loadResultsLog(this.$root)
      let resultsLog = this.currentResultsLog
      if (!resultsLog || !resultsLog.results) {
        return
      }
      let ephemerides = resultsLog.results.threedEphemerides
      let fieldIndexes = resultsLog.results.threedFieldIndexes
      this.data = dataTools.convertSimulationData(ephemerides, fieldIndexes)
      this.generalData = dataTools.convertSimulationData(
        resultsLog.results.ephemerides,
        resultsLog.results.fieldIndexes
      )
    },

    initSampledProperties() {
      this.sampledRemainingPropellant = new Cesium.SampledProperty(Number)
      this.thrustManeuvers = new Cesium.TimeIntervalCollectionProperty()
      this.sampledBatteryState = new Cesium.SampledProperty(Number)
      this.eclipseIntervals = new Cesium.TimeIntervalCollectionProperty()
    },

    setInitialDate(initialDateString) {
      this.initialDate = new Date(Date.parse(initialDateString))
      this.finalDate = new Date(
        this.initialDate.getTime() +
          1000 * this.data[this.data.length - 1].simulationDuration
      )
    },

    addMainDataSource() {
      // add datasource to viewer. This allows the display of S/C model, axes, sun direction, etc.
      this.viewer.dataSources.add(this.createDataSource()).then((ds) => {
        // this is used to set the tracked entity
        this.entities = ds.entities
      })
      this.entities.values.forEach((el) => {
        // we don't want the polylines and ref points to appear in the list
        if (
          !(el.id.includes('invisibleREF') || el.name.includes('invisibleREF'))
        ) {
          this.entitiesList.push({ id: el.id, name: el.name })
        }
        if (el.id.includes('Spacecraft')) {
          this.satellitesEvents[0].satId = el.id
        }
      })
    },

    attachSatStateUpdateEventListener() {
      // init satellite state values
      this.lastUpdated = Cesium.JulianDate.fromDate(this.initialDate)
      this.satelliteState.battery =
        this.sampledBatteryState.getValue(this.lastUpdated) * 100

      this.satelliteState.remainingPropellant =
        this.sampledRemainingPropellant.getValue(this.lastUpdated)

      this.satelliteState.remainingPropellantPercentage =
        (this.satelliteState.remainingPropellant /
          this.sampledRemainingPropellant.getValue(
            Cesium.JulianDate.fromDate(this.initialDate)
          )) *
        100

      // init intermediate values to update satellite state
      let thrustManeuver = {
        burning: false,
        start: new Date(),
        end: new Date()
      }

      // this will update the above values every second
      this.viewer.clock.onTick.addEventListener((clock) =>
        this.updateSatelliteState(clock, thrustManeuver)
      )
    },

    updateSatelliteState(clock, thrustManeuver, force = false) {
      // compute time passed in seconds since last update
      const timePassed = Cesium.JulianDate.secondsDifference(
        clock.currentTime,
        this.lastUpdated
      )

      // if time passed > 1 second of real time (or if update is forced):
      if (Math.abs(timePassed) > Math.abs(clock.multiplier) || force) {
        // retrieves battery level
        this.satelliteState.battery =
          this.sampledBatteryState.getValue(clock.currentTime) * 100
        // retrieves current thrust maneuver
        thrustManeuver = this.thrustManeuvers.getValue(clock.currentTime)
        // updates remaining propellant
        this.satelliteState.remainingPropellant =
          this.sampledRemainingPropellant.getValue(clock.currentTime)
        // computes percentage
        this.satelliteState.remainingPropellantPercentage =
          (this.satelliteState.remainingPropellant /
            this.sampledRemainingPropellant.getValue(
              Cesium.JulianDate.fromDate(this.initialDate)
            )) *
          100

        // updates all progress bars in timeline
        this.satellitesEvents.forEach((oneSatEvents) => {
          oneSatEvents.burns.forEach((burn) => {
            if (
              Cesium.JulianDate.compare(
                clock.currentTime,
                Cesium.JulianDate.fromIso8601(burn.end)
              ) > 0
            ) {
              burn.progress = 100
            } else if (
              Cesium.JulianDate.compare(
                clock.currentTime,
                Cesium.JulianDate.fromIso8601(burn.start)
              ) <= 0
            ) {
              burn.progress = 0
            } else if (thrustManeuver && burn.id === thrustManeuver.id) {
              burn.progress = this.burnProgress
            }
          })
        })
        this.lastUpdated = clock.currentTime
      }
    },

    beforeDestroy() {
      this.viewer.entities.removeAll()
      this.viewer.destroy()
      this.viewer = null
      window.removeEventListener(this.updatePositions)
      this.viewer.clock.removeEventListener(this.updateSatelliteState)
      delete this.cartesianData
      this.cartesianData = []
      delete this.data
      delete this.czml
      delete this.sampledQuaternions
      delete this.sampledXAxisVertexPos
      delete this.sampledYAxisVertexPos
      delete this.sampledZAxisVertexPos
    }
  }
}
</script>
